import { FC } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormHelperText from "@mui/material/FormHelperText";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { ValidationType } from "../../domain/event.interface";
import {
	ValidationAgentBinding,
	ValidationAgentEntry,
} from "../../domain/validationAgent.interface";

/**
 * Bind a validation agent to an event — SIMS-8223, design frame `3b`.
 *
 * Four states, all drawn: catalog open, no agent selected, empty catalog, and
 * an existing binding made incompatible by a change to the event's validation
 * type.
 */

export const NO_AGENT = "";

/** Copy read from the frame, kept together so it cannot drift apart. */
export const AGENT_PICKER_COPY = {
	label: "Validation agent",
	none: "None — no agent validation",
	noneDescription: "Submissions follow the existing manual path.",
	unbound:
		"Submissions for this event are not agent-validated. They follow the manual review path.",
	/**
	 * Matches the frame. An earlier version of this file made the control
	 * type-scoped, on the reading that the absolute wording contradicts AC9. The
	 * crop shows the control and its helper together and they are coherent: the
	 * control is a terse placeholder in a DISABLED select and the helper carries
	 * the precision, which is always present. The contradiction was mine.
	 */
	empty: "No agents available",
	emptyHelper: "No validation agents are compatible with this event type.",
	/** Inline, under the select. */
	incompatibleField: "This agent isn't compatible with this event type.",
	clear: "Clear agent",
	chooseAnother: "Choose another agent",
	agentLabel: "Validation agent",
	agentHelper:
		"Optional. Catalog is filtered to agents compatible with the validation type.",
	compatibleWith: "Compatible with",
	change: "Change",
	dormant:
		"This agent is not currently running. Submissions for this event follow the manual review path until it is available again. The binding is unchanged — you do not need to clear it.",
} as const;

/**
 * "Meal photo agent supports Photo and Compound events." — the frame names the
 * agent AND what it does support, so the reader can act without going to look
 * it up. Built here rather than as a fixed string for that reason.
 */
export const incompatibleMessage = (
	name: string,
	supported: ValidationType[],
): string => {
	const list =
		supported.length > 1
			? `${supported.slice(0, -1).join(", ")} and ${supported[supported.length - 1]}`
			: supported[0];
	return `${name} supports ${list} events. Choose a compatible agent or clear the selection to save.`;
};

interface ValidationAgentPickerProps {
	/** What may be NEWLY bound — already parity- and type-filtered by the server. */
	agents: ValidationAgentEntry[];
	/**
	 * The whole catalog, used ONLY to describe an existing binding the filtered
	 * list cannot contain. See `useValidationAgentCatalog` for why both are
	 * needed.
	 */
	catalog?: ValidationAgentEntry[];
	binding?: ValidationAgentBinding;
	validationType: ValidationType;
	isLoading?: boolean;
	onChange: (binding: ValidationAgentBinding | undefined) => void;
}

/**
 * Whether an EXISTING binding still fits the event's validation type.
 *
 * Mirrors the backend's `isBindingStillCompatible`, and exists for the same
 * reason: the picker only ever offers compatible agents, so this state is
 * reachable only by changing the validation type underneath a binding nobody
 * touched. Save is blocked until it is resolved (AC4).
 *
 * An agent the catalog no longer returns is NOT treated as incompatible — it
 * may simply be retired or its implementation undeployed, which is AC5's
 * problem with a different answer. Telling someone to clear a binding that is
 * merely dormant would lose its configuration for no reason.
 */
export const findEntry = (
	entries: ValidationAgentEntry[],
	binding: ValidationAgentBinding | undefined,
): ValidationAgentEntry | undefined =>
	binding
		? entries.find(
				(a) => a.agentKey === binding.agentKey && a.version === binding.version,
			)
		: undefined;

/**
 * Whether an EXISTING binding still fits the event's validation type.
 *
 * 🔴 Must be given the FULL catalog, not the offered list. The offered list is
 * type-filtered by the server, so an incompatible agent is never in it — reading
 * this from that list would make the state permanently undetectable, which is
 * the bug this signature exists to prevent.
 *
 * An agent absent from the full catalog is NOT incompatible: it may be retired
 * or its implementation undeployed, which is AC5's problem with a different
 * answer. Telling someone to clear a merely-dormant binding would lose its
 * configuration for nothing.
 */
export const isBindingIncompatible = (
	catalog: ValidationAgentEntry[],
	binding: ValidationAgentBinding | undefined,
	validationType: ValidationType,
): boolean => {
	const entry = findEntry(catalog, binding);
	if (!entry) return false;
	return !entry.supportedValidationTypes.includes(validationType);
};

/**
 * Whether a bound agent looks like it will not RUN — SIMS-8223 AC5.
 *
 * 🔴 Derived, because the client is never told directly. The offered list is
 * what the server says may be newly bound: it applies parity (is an
 * implementation registered?) and status (is the entry retired?) as well as
 * type. So an agent that is in the full catalog, IS compatible with this
 * event's type, and is still absent from the offered list can only be missing
 * an implementation or retired — which is exactly AC5's case.
 *
 * Deliberately distinct from incompatibility: that one asks the designer to
 * change the binding, this one does not. The binding is fine; the platform is
 * not currently able to run it, and clearing it would lose configuration for a
 * problem the designer did not cause and cannot fix.
 */
export const isBindingDormant = (
	catalog: ValidationAgentEntry[],
	offered: ValidationAgentEntry[],
	binding: ValidationAgentBinding | undefined,
	validationType: ValidationType,
): boolean => {
	const entry = findEntry(catalog, binding);
	if (!entry) return false;
	if (!entry.supportedValidationTypes.includes(validationType)) return false;
	return !findEntry(offered, binding);
};

const optionValue = (entry: ValidationAgentEntry): string =>
	`${entry.agentKey}@${entry.version}`;

export const ValidationAgentPicker: FC<ValidationAgentPickerProps> = ({
	agents,
	catalog,
	binding,
	validationType,
	isLoading = false,
	onChange,
}) => {
	const catalogEmpty = !isLoading && agents.length === 0;
	const fullCatalog = catalog ?? agents;
	const incompatible = isBindingIncompatible(
		fullCatalog,
		binding,
		validationType,
	);
	const boundEntry = findEntry(fullCatalog, binding);
	const dormant = isBindingDormant(
		fullCatalog,
		agents,
		binding,
		validationType,
	);
	const selected = binding
		? `${binding.agentKey}@${binding.version}`
		: NO_AGENT;

	const handleChange = (value: string): void => {
		if (value === NO_AGENT) {
			onChange(undefined);
			return;
		}
		const entry = agents.find((a) => optionValue(a) === value);
		if (!entry) return;
		// A new binding starts with no config — the values belong to the agent
		// that was chosen, and carrying the previous agent's over would produce
		// a configuration nobody authored.
		onChange({ agentKey: entry.agentKey, version: entry.version, config: {} });
	};

	return (
		<Box>
			<TextField
				select
				fullWidth
				size="small"
				// 🔴 The label stays visible even when disabled, and the reason
				// lives in persistent helper text rather than a tooltip or the grey
				// fill alone — the platform rule Qori confirmed in the v1.6
				// addendum, and what makes the empty state readable.
				label={AGENT_PICKER_COPY.label}
				value={catalogEmpty ? NO_AGENT : selected}
				disabled={catalogEmpty || isLoading}
				error={incompatible}
				onChange={(event) => handleChange(event.target.value)}
				SelectProps={{
					displayEmpty: true,
					renderValue: (value) => {
						if (catalogEmpty) return AGENT_PICKER_COPY.empty;
						if (value === NO_AGENT) return AGENT_PICKER_COPY.none;
						const entry = agents.find((a) => optionValue(a) === value);
						return entry ? entry.name : String(value);
					},
				}}
			>
				<MenuItem value={NO_AGENT}>
					<ListItemText
						primary={AGENT_PICKER_COPY.none}
						secondary={AGENT_PICKER_COPY.noneDescription}
					/>
				</MenuItem>
				{agents.map((entry) => (
					<MenuItem key={optionValue(entry)} value={optionValue(entry)}>
						<Box>
							<ListItemText
								primary={entry.name}
								secondary={entry.description}
							/>
							{/* The declared compatibility, shown rather than only
							    enforced — a designer can see WHY an agent is on offer. */}
							<Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
								{entry.supportedValidationTypes.map((type) => (
									<Chip key={type} label={type} size="small" />
								))}
							</Stack>
						</Box>
					</MenuItem>
				))}
			</TextField>

			{catalogEmpty && (
				<FormHelperText>{AGENT_PICKER_COPY.emptyHelper}</FormHelperText>
			)}

			{/* The frame carries a persistent helper under the control whenever it
			    is usable — it explains WHY the list is short before anyone wonders. */}
			{!catalogEmpty && !incompatible && (
				<FormHelperText>{AGENT_PICKER_COPY.agentHelper}</FormHelperText>
			)}

			{incompatible && (
				<FormHelperText error>
					{AGENT_PICKER_COPY.incompatibleField}
				</FormHelperText>
			)}

			{!catalogEmpty && !isLoading && !binding && (
				<Alert severity="info" sx={{ mt: 1 }}>
					{AGENT_PICKER_COPY.unbound}
				</Alert>
			)}

			{/* The bound-agent summary from `3a`: what was chosen, what it does,
			    what it supports, and WHICH VERSION — AC7's only visible surface. */}
			{boundEntry && !incompatible && (
				<Box
					sx={{
						mt: 1,
						p: 1.5,
						borderRadius: 1,
						bgcolor: "action.hover",
						display: "flex",
						alignItems: "flex-start",
						gap: 1,
					}}
				>
					<Box sx={{ flexGrow: 1 }}>
						<Typography variant="titleSmall" sx={{ display: "block" }}>
							{boundEntry.name}
						</Typography>
						<Typography
							variant="bodySmall"
							color="text.secondary"
							sx={{ display: "block" }}
						>
							{boundEntry.description}
						</Typography>
						<Stack
							direction="row"
							spacing={0.5}
							sx={{ mt: 0.5 }}
							alignItems="center"
						>
							<Typography variant="bodySmall" color="text.secondary">
								{AGENT_PICKER_COPY.compatibleWith}
							</Typography>
							{boundEntry.supportedValidationTypes.map((type) => (
								<Chip key={type} label={type} size="small" />
							))}
						</Stack>
					</Box>
					{/* AC7 — the version this binding was made against. A newly
					    published version must not change it, so it is shown rather
					    than implied. */}
					<Chip label={`v${boundEntry.version}`} size="small" />
				</Box>
			)}

			{/* AC5 — the binding is sound but nothing will run it. A warning, not
			    an error: the designer did not cause this and cannot fix it from
			    here, and the submission path degrades to manual review on its own.
			    It is mirrored here because an audit entry nobody opens is not a
			    warning. */}
			{dormant && boundEntry && (
				<Alert severity="warning" sx={{ mt: 1 }}>
					{AGENT_PICKER_COPY.dormant}
				</Alert>
			)}

			{incompatible && boundEntry && (
				<Alert severity="error" sx={{ mt: 1 }}>
					{incompatibleMessage(
						boundEntry.name,
						boundEntry.supportedValidationTypes,
					)}
				</Alert>
			)}

			{incompatible && (
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
					<Button
						variant="outlined"
						size="small"
						onClick={() => onChange(undefined)}
					>
						{AGENT_PICKER_COPY.clear}
					</Button>
					<Typography variant="bodySmall" color="text.secondary">
						{AGENT_PICKER_COPY.chooseAnother}
					</Typography>
				</Stack>
			)}
		</Box>
	);
};

export default ValidationAgentPicker;
